import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
// PDF export uses the backend /api/export/pdf endpoint (ReportLab + Noto Sans Devanagari)
// so Hindi (Devanagari) text renders correctly. No client-side jsPDF needed for the table export.
import * as XLSX from "xlsx";
import {
  Camera, Upload, X, RotateCcw, Loader2, Sparkles,
  Download, Copy, FileSpreadsheet, ArrowRight, Check, ImageIcon,
  MessageCircle, FileText, FileType2
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Section, Eyebrow, H1, Lead, FadeIn } from "../components/Primitives";
import { addHistoryItem } from "../lib/localdb";
import HistorySidebar from "../components/HistorySidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 8 * 1024 * 1024; // 8 MB cap per image (network sanity, not a feature limit)

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const result = String(r.result || "");
      const idx = result.indexOf(",");
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

function downscaleImage(file, maxDim = 1600, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const scale = Math.min(1, maxDim / Math.max(w, h));
      const cw = Math.max(1, Math.round(w * scale));
      const ch = Math.max(1, Math.round(h * scale));
      const canvas = document.createElement("canvas");
      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, cw, ch);
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error("Image conversion failed"));
        resolve({ blob, mime: "image/jpeg" });
      }, "image/jpeg", quality);
    };
    img.onerror = () => reject(new Error("Could not load image"));
    img.src = URL.createObjectURL(file);
  });
}

const REQUIRED_COUNT = 0; // unused, kept to minimise downstream churn

export default function TryPage() {
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);
  // picked: array of { file: Blob, mime: string, previewUrl: string }
  const [picked, setPicked] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState(null);
  const [historyTick, setHistoryTick] = useState(0);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;

    // Validate types/sizes — NO count cap (unlimited photos)
    const valid = [];
    for (const f of files) {
      if (!/^image\/(jpe?g|png|webp)$/i.test(f.type)) {
        toast.error(`Skipped "${f.name}" — not a JPEG/PNG/WEBP image.`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`Skipped "${f.name}" — over 8 MB.`);
        continue;
      }
      valid.push(f);
    }
    if (valid.length === 0) return;

    try {
      const processed = await Promise.all(
        valid.map(async (file) => {
          const { blob, mime } = await downscaleImage(file);
          return { file: blob, mime, previewUrl: URL.createObjectURL(blob) };
        })
      );
      setPicked((prev) => [...prev, ...processed]);
      setResult(null);
    } catch (err) {
      toast.error("Could not read one of the images. Please try again.");
    }
  };

  const removeAt = (idx) => {
    setPicked((prev) => {
      const p = prev[idx];
      if (p) URL.revokeObjectURL(p.previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
    setResult(null);
  };

  const reset = () => {
    picked.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    setPicked([]);
    setResult(null);
    setProgress({ done: 0, total: 0 });
  };

  const digitize = async () => {
    if (picked.length < 1) {
      toast.error(`Please add at least 1 photo first.`);
      return;
    }
    setLoading(true);
    setResult(null);
    setProgress({ done: 0, total: picked.length });
    try {
      const responses = await Promise.all(
        picked.map(async (p) => {
          const image_base64 = await fileToBase64(p.file);
          const { data } = await axios.post(
            `${API}/digitize`,
            {
              image_base64,
              mime_type: p.mime,
            },
            { timeout: 120_000, withCredentials: true }
          );
          setProgress((prev) => ({ done: prev.done + 1, total: prev.total }));
          return data;
        })
      );
      // Merge: use first response's columns; append all rows in order.
      const cols = responses[0]?.columns || [];
      const mergedRows = [];
      responses.forEach((r) => {
        for (const row of r.rows || []) {
          const padded = [...row];
          while (padded.length < cols.length) padded.push("");
          mergedRows.push(padded.slice(0, cols.length));
        }
      });
      const newResult = {
        id: responses[0]?.id || "merged",
        register_type: responses[0]?.register_type || "Register",
        columns: cols,
        rows: mergedRows,
        notes:
          (responses[0]?.notes || "") +
          ` Merged from ${picked.length} photograph${picked.length === 1 ? "" : "s"} (${mergedRows.length} total rows).`,
      };
      setResult(newResult);
      toast.success(`Digitized ${picked.length} photo${picked.length === 1 ? "" : "s"} · ${mergedRows.length} rows extracted`);

      // Zero-Server: save to encrypted local DB (never sent back to our server).
      try {
        await addHistoryItem({
          kind: "scan",
          title: `${newResult.register_type} · ${mergedRows.length} rows`,
          payload: newResult,
        });
        setHistoryTick((t) => t + 1);
      } catch (e) {
        console.warn("Could not save to local history:", e);
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not digitize one of the images. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const baseFilename = () =>
    `${(result?.register_type || "register").replace(/\s+/g, "_").toLowerCase()}`;

  const downloadCSV = () => {
    if (!result) return;
    const rows = [result.columns, ...(result.rows || [])];
    const csv = rows.map((r) =>
      r.map((cell) => {
        const s = String(cell ?? "");
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(",")
    ).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${baseFilename()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return blob;
  };

  const downloadPDF = async () => {
    if (!result) return;
    const tId = toast.loading("Generating PDF…");
    try {
      const res = await axios.post(
        `${API}/export/pdf`,
        {
          title: result.register_type || "Register",
          columns: result.columns || [],
          rows: result.rows || [],
          notes: result.notes || "",
          filename: baseFilename(),
        },
        {
          responseType: "blob",
          withCredentials: true,
          timeout: 60_000,
          headers: { Accept: "application/pdf" },
        },
      );
      if (!(res.data instanceof Blob) || res.data.size === 0) {
        throw new Error("Empty PDF returned by server.");
      }
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${baseFilename()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded", { id: tId });
    } catch (err) {
      // Build a specific, actionable error message instead of axios's vague "Network Error".
      let message;
      if (err?.code === "ECONNABORTED") {
        message = "PDF generation timed out. Try again with a smaller register.";
      } else if (err?.response) {
        // Server responded with an error status
        const status = err.response.status;
        if (status === 404) {
          message =
            "PDF export endpoint not found. Your app version may be outdated — please redeploy/refresh.";
        } else if (status >= 500) {
          // Server error — try to read the JSON detail out of the blob
          try {
            const text = await err.response.data.text?.();
            const j = text ? JSON.parse(text) : null;
            message = j?.detail || `Server error (${status}). Please try again.`;
          } catch {
            message = `Server error (${status}). Please try again.`;
          }
        } else {
          message = `PDF export failed with HTTP ${status}.`;
        }
      } else if (err?.message?.toLowerCase().includes("network")) {
        message =
          "Could not reach the PDF service. Check your internet connection, then try again.";
      } else {
        message = err?.message || "PDF download failed.";
      }
      // Log full details to console for debugging
      console.error("PDF export error:", err);
      toast.error(message, { id: tId, duration: 6000 });
    }
  };

  const downloadXLSX = () => {
    if (!result) return;
    const aoa = [result.columns, ...(result.rows || [])];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = result.columns.map((c) => ({
      wch: Math.max(12, String(c).length + 2,
        ...(result.rows || []).map((r) => String(r[result.columns.indexOf(c)] || "").length + 2))
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, result.register_type?.slice(0, 28) || "Register");
    XLSX.writeFile(wb, `${baseFilename()}.xlsx`);
    toast.success("Excel file downloaded");
  };

  const exportToSheets = () => {
    if (!result) return;
    downloadCSV();
    window.open("https://sheets.new", "_blank", "noopener,noreferrer");
    toast.success("CSV downloaded. Drag it into the new Google Sheets tab to import.", { duration: 6000 });
  };

  const shareToWhatsApp = () => {
    if (!result) return;
    const header = `*${result.register_type}*\n_${new Date().toLocaleDateString()}_\n`;
    const lines = [result.columns.join(" | "), "-".repeat(40)];
    for (const r of result.rows || []) lines.push(r.join(" | "));
    let body = `${header}\n${lines.join("\n")}\n\n— via Register to Digital`;
    // wa.me URL limit ~4096 chars after encoding; trim defensively
    const MAX = 1500;
    if (body.length > MAX) body = body.slice(0, MAX) + "\n…(truncated)";
    const url = `https://wa.me/?text=${encodeURIComponent(body)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyTable = async () => {
    if (!result) return;
    const lines = [result.columns.join("\t"), ...result.rows.map((r) => r.join("\t"))];
    await navigator.clipboard.writeText(lines.join("\n"));
    toast.success("Copied to clipboard");
  };

  return (
    <div data-testid="page-try">
      <Section className="pt-24 pb-10">
        <FadeIn><Eyebrow>Try it now — free, no signup</Eyebrow></FadeIn>
        <FadeIn delay={0.05}>
          <H1 className="mt-6 max-w-3xl">Photograph your register. Get a digital table.</H1>
        </FadeIn>
        <FadeIn delay={0.1}>
          <Lead className="mt-6">Upload as many photos as you have — there&apos;s no limit. Our AI auto-detects the layout and gives you one clean digital table.</Lead>
        </FadeIn>
      </Section>

      <Section className="pt-0">
        <div className="flex gap-6">
          <HistorySidebar
            kind="scan"
            refreshKey={historyTick}
            onLoad={(payload) => { if (payload) setResult(payload); }}
          />
          <div className="min-w-0 flex-1">
        <div className="grid gap-6 lg:grid-cols-12">
          {/* Capture panel */}
          <div className="lg:col-span-5">
            <div className="rounded-3xl border border-zinc-200 bg-white p-6">
              <div className="flex items-center justify-between">
                <div className="text-xs font-medium uppercase tracking-wider text-zinc-500">Step 1 · Add your photos</div>
                {picked.length > 0 && (
                  <button
                    data-testid="try-reset"
                    onClick={reset}
                    className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-950"
                  >
                    <RotateCcw className="h-3.5 w-3.5" /> Reset
                  </button>
                )}
              </div>

              {picked.length === 0 ? (
                <div className="mt-4">
                  <div className="aspect-[4/3] w-full rounded-2xl border-2 border-dashed border-zinc-300 bg-zinc-50 grid place-items-center text-zinc-400">
                    <div className="text-center">
                      <ImageIcon className="mx-auto h-8 w-8" />
                      <p className="mt-3 font-medium text-zinc-600">Add as many photos as you like</p>
                      <p className="mt-1 text-xs">Free, no limits · JPEG, PNG or WEBP · max 8 MB each</p>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <Button
                      data-testid="try-open-camera"
                      onClick={() => cameraInputRef.current?.click()}
                      className="rounded-full bg-zinc-950 text-white hover:bg-zinc-800"
                    >
                      <Camera className="mr-1.5 h-4 w-4" /> Open camera
                    </Button>
                    <Button
                      data-testid="try-upload"
                      onClick={() => fileInputRef.current?.click()}
                      variant="outline"
                      className="rounded-full border-zinc-300"
                    >
                      <Upload className="mr-1.5 h-4 w-4" /> Upload images
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-4">
                  <div data-testid="try-thumbnails" className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2">
                    {picked.map((p, i) => (
                      <div
                        key={i}
                        data-testid={`try-thumbnail-${i}`}
                        className="group relative aspect-square overflow-hidden rounded-xl border border-zinc-200 bg-zinc-100"
                      >
                        <img
                          src={p.previewUrl}
                          alt={`Register ${i + 1}`}
                          className="absolute inset-0 h-full w-full object-cover"
                        />
                        <span className="absolute left-1 top-1 rounded-full bg-zinc-950/85 px-1.5 py-0.5 text-[10px] font-medium text-white">
                          {i + 1}
                        </span>
                        <button
                          type="button"
                          data-testid={`try-thumbnail-remove-${i}`}
                          onClick={() => removeAt(i)}
                          aria-label={`Remove image ${i + 1}`}
                          className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-zinc-950/85 text-white opacity-0 transition-opacity group-hover:opacity-100"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      data-testid="try-add-more"
                      onClick={() => fileInputRef.current?.click()}
                      className="aspect-square rounded-xl border-2 border-dashed border-zinc-300 bg-zinc-50 text-zinc-500 transition hover:border-zinc-950 hover:text-zinc-950 grid place-items-center"
                    >
                      <div className="text-center">
                        <Upload className="mx-auto h-4 w-4" />
                        <p className="mt-1 text-[10px] font-medium leading-tight">Add<br />more</p>
                      </div>
                    </button>
                  </div>
                  <p data-testid="try-picked-count" className="mt-3 text-xs text-zinc-600">
                    <span className="font-semibold text-zinc-950">{picked.length}</span> photo{picked.length === 1 ? "" : "s"} ready.
                  </p>
                </div>
              )}

              {/* Hidden inputs */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                multiple
                onChange={onPick}
                className="hidden"
                data-testid="try-camera-input"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={onPick}
                className="hidden"
                data-testid="try-file-input"
              />

              {/* Step 2 + digitize */}
              <div className="mt-6">
                <Button
                  data-testid="try-digitize"
                  onClick={digitize}
                  disabled={picked.length < 1 || loading}
                  className="mt-2 w-full rounded-full bg-zinc-950 text-white hover:bg-zinc-800"
                >
                  {loading ? (
                    <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Reading {progress.done}/{progress.total} register{progress.total === 1 ? "" : "s"}…</>
                  ) : (
                    <><Sparkles className="mr-1.5 h-4 w-4" /> Digitize {picked.length || ""} photo{picked.length === 1 ? "" : "s"}</>
                  )}
                </Button>
                <p className="mt-3 text-[11px] text-zinc-500">
                  We pick the best settings automatically — no questions, no setup. Your photos are discarded after processing.
                </p>
              </div>
            </div>
          </div>

          {/* Result panel */}
          <div className="lg:col-span-7">
            <div className="rounded-3xl border border-zinc-200 bg-white p-6 min-h-full">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wider text-zinc-500">Step 3 · Digital output</div>
                  <div className="mt-1 font-poppins text-lg font-semibold text-zinc-950">
                    {result ? result.register_type : "Awaiting input…"}
                  </div>
                </div>
                {result && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button data-testid="try-copy" onClick={copyTable} variant="outline" size="sm" className="rounded-full border-zinc-300">
                      <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy
                    </Button>
                  </div>
                )}
              </div>

              {result && (
                <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Button
                    data-testid="export-pdf"
                    onClick={downloadPDF}
                    className="rounded-full bg-zinc-950 text-white hover:bg-zinc-800"
                  >
                    <FileText className="mr-1.5 h-3.5 w-3.5" /> PDF
                  </Button>
                  <Button
                    data-testid="export-excel"
                    onClick={downloadXLSX}
                    variant="outline"
                    className="rounded-full border-zinc-300"
                  >
                    <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5" /> Excel
                  </Button>
                  <Button
                    data-testid="export-sheets"
                    onClick={exportToSheets}
                    variant="outline"
                    className="rounded-full border-zinc-300"
                  >
                    <FileType2 className="mr-1.5 h-3.5 w-3.5" /> Sheets
                  </Button>
                  <Button
                    data-testid="export-whatsapp"
                    onClick={shareToWhatsApp}
                    variant="outline"
                    className="rounded-full border-zinc-300"
                  >
                    <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> WhatsApp
                  </Button>
                </div>
              )}

              <div className="mt-5">
                {!result && !loading && (
                  <EmptyState />
                )}
                {loading && <LoadingState />}
                {result && <ResultTable result={result} />}
              </div>

              {result && (
                <div className="mt-6 rounded-2xl border border-zinc-200 bg-zinc-50 p-5">
                  <div className="flex items-start gap-3">
                    <Sparkles className="h-5 w-5 text-zinc-700" />
                    <div className="text-sm text-zinc-700">
                      <strong className="font-semibold text-zinc-950">Free forever.</strong>{" "}
                      Upload as many photos as you need — no limits, no payments. Tell a friend.
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Link to="/contact" data-testid="try-result-contact">
                      <Button size="sm" variant="outline" className="rounded-full border-zinc-300">Send feedback</Button>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
          </div>
        </div>
      </Section>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="grid place-items-center rounded-2xl border-2 border-dashed border-zinc-200 bg-zinc-50 px-6 py-16 text-center">
      <div className="grid h-12 w-12 place-items-center rounded-full bg-white border border-zinc-200">
        <Sparkles className="h-5 w-5 text-zinc-700" />
      </div>
      <p className="mt-4 max-w-md text-sm text-zinc-600">
        Pick <span className="font-medium text-zinc-950">one or more photos</span> on the left and click{" "}
        <span className="font-medium text-zinc-950">Digitize</span>.
        Your merged digital table will appear here.
      </p>
    </div>
  );
}

function LoadingState() {
  const lines = ["Detecting columns…", "Reading handwriting…", "Structuring rows…", "Almost there…"];
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-6">
      <div className="flex items-center gap-3">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-700" />
        <span className="text-sm text-zinc-700">Reading your register…</span>
      </div>
      <ul className="mt-4 space-y-2">
        {lines.map((l, i) => (
          <li key={l} className="flex items-center gap-2 text-xs text-zinc-500" style={{ animationDelay: `${i * 200}ms` }}>
            <Check className="h-3.5 w-3.5 text-zinc-300" /> {l}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ResultTable({ result }) {
  const { columns, rows, notes } = result;
  if (!columns?.length) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white p-6 text-sm text-zinc-600">
        We could not detect any columns in this image. Try a clearer, well-lit photograph.
      </div>
    );
  }
  return (
    <div data-testid="try-result-table" className="overflow-hidden rounded-2xl border border-zinc-200">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[480px] border-collapse">
          <thead>
            <tr className="bg-zinc-950 text-white">
              {columns.map((c, i) => (
                <th
                  key={i}
                  className="px-4 py-3 text-left font-poppins text-xs font-semibold uppercase tracking-wider"
                  style={{ fontFamily: '"Noto Sans Devanagari", system-ui, sans-serif' }}
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-6 text-center text-sm text-zinc-500">
                  No rows detected.
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={i} className={i % 2 ? "bg-zinc-50" : "bg-white"}>
                  {columns.map((_, j) => (
                    <td
                      key={j}
                      className="px-4 py-3 text-sm text-zinc-800 align-top whitespace-pre-wrap break-words"
                      style={{ fontFamily: '"Noto Sans Devanagari", system-ui, sans-serif' }}
                    >
                      {r[j] ?? ""}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {notes && (
        <div className="border-t border-zinc-200 bg-zinc-50 px-4 py-3 text-xs text-zinc-600">
          <span className="font-medium text-zinc-700">Note:</span> {notes}
        </div>
      )}
    </div>
  );
}

